import classNames from "classnames/bind"
import style from './index.module.scss'
import { useTranslation } from "react-i18next"
import { Button, Card, Col, Empty, Form, Image, Row, Upload, notification } from "antd"
import { useEffect, useMemo, useState, useCallback } from "react"
import { getUser, handleUploadImage, normFile } from "utils/index"
import shopApi from "api/shop"
import { CertificateFilter, ModalDelete } from "./components"
import { rolesGetAllShop } from "./roles"
import certificateApi from "api/certificate"
import { EditOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons"
import imageApi from "api/image"
import { roles } from "constants/index"

const cx = classNames.bind(style)
export default function CertificatePage() {
    const [formSearch] = Form.useForm()
    const [formUpload] = Form.useForm()
    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();

    const [shops, setShops] = useState([])
    const [loading, setLoading] = useState(false)
    const [certificates, setCertificates] = useState([])


    const openNotificationWithIcon = (type, message, description) => {
        api[type]({ message, description });
    };

    const shopInfo = useMemo(() => {
        return shops.find((shop) => shop.value === formSearch.getFieldValue('shop'))
    }, [formSearch, shops])

    const getStore = useCallback(async () => {
        try {
            const res = await shopApi.getShopByRole();
            if (rolesGetAllShop.includes(+getUser().role)) {
                setShops(
                    res.shops.map((shop) => ({
                        value: shop.shopId,
                        label: shop.shopName,
                        cabinet_code_1: shop.cabinet_code_1,
                        cabinet_code_2: shop.cabinet_code_2,
                    }))
                );
            } else {
                setShops(
                    res.shops.map((shop) => ({
                        value: shop.shop._id,
                        label: shop.shop.name,
                        cabinet_code_1: shop.shop.cabinet_code_1,
                        cabinet_code_2: shop.shop.cabinet_code_2,
                    }))
                );
                formSearch.setFieldsValue({
                    shop: res.shops[0].shop._id
                });
                formSearch.submit();
            }
        } catch (error) {

        }
    }, [formSearch]);

    const onSearch = useCallback(async (values = formSearch.getFieldsValue()) => {
        try {
            setLoading(true)
            const res = await certificateApi.getAll({
                shop: values.shop,
            })
            setCertificates(res.data)
        } catch (error) {
            openNotificationWithIcon('error', t('error'), t('hasError'))
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [formSearch, openNotificationWithIcon, t])

    const handleFileSelect = useCallback(async (file, oldFile) => {
        try {
            setLoading(true)
            if (oldFile?._id) {
                // delete old file if exists
                oldFile.fileId && await imageApi.deleteImage(oldFile.fileId);
                const { success, result } = await handleUploadImage(file, true);
                if (!success) {
                    openNotificationWithIcon('error', t('error'), t('hasError'));
                    return;
                }
                await certificateApi.update(oldFile._id, {
                    fileId: result.public_id,
                    fileUrl: result.url,
                    shopId: shopInfo.value
                });
                openNotificationWithIcon("success", t('notification'), t('success'));
                onSearch()
                formUpload.resetFields();
            } else {
                const { success, result } = await handleUploadImage(file);
                if (!success) {
                    openNotificationWithIcon("error", t('error'), t('hasError'));
                    return false;
                }
                const res = await certificateApi.create({
                    fileId: result.public_id,
                    fileUrl: result.url,
                    shop: shopInfo.value,
                });
                if (res?.success) {
                    openNotificationWithIcon("success", t('notification'), t('success'));
                    onSearch()
                    formUpload.resetFields();
                }
            }
        } catch (error) {
            openNotificationWithIcon('error', t('error'), t('hasError'));
            console.error(error);
        } finally {
            setLoading(false);
        }
        return false; // Prevent default upload behavior
    }, [openNotificationWithIcon, t, shopInfo, onSearch, formUpload])

    const UploadFileRC = ({ oldFile }) => (
        <Form form={formUpload} layout="vertical">
            <Form.Item
                name="certImg"
                valuePropName="fileList"
                getValueFromEvent={normFile}
            >
                <Upload
                    name="img"
                    maxCount={1}
                    listType={oldFile?._id ? "" : "picture-card"}
                    accept="image/png, image/jpg, image/jpeg"
                    showUploadList={false}
                    beforeUpload={(file) => handleFileSelect(file, oldFile)}
                    disabled={loading}
                    className={cx(oldFile?._id ? '' : "upload-box")}
                >
                    {
                        oldFile?._id ?
                            <Button><EditOutlined /></Button> :
                            <button
                                style={{ color: 'inherit', cursor: 'inherit', border: 0, background: 'none' }}
                                type="button"
                            >
                                {loading ? <LoadingOutlined /> : <UploadOutlined />}
                                <div style={{ marginTop: 8 }}>
                                    {loading ? 'Uploading...' : 'Upload'}
                                </div>
                            </button>
                    }
                </Upload>
            </Form.Item>
        </Form>
    );


    useEffect(() => {
        getStore()
    }, [getStore])

    return (
        <>
            {contextHolder}
            <div className={cx("title")}>
                <p>{t('certificate')}</p>
            </div>
            <CertificateFilter form={formSearch} shops={shops} onFinish={onSearch} loading={loading} />
            <Row gutter={16}>
                {
                    certificates.length > 0 ? certificates.map((item) => (
                        <Col span={6} key={item._id}>
                            <Card
                                className={cx("card-cert")}
                                cover={
                                    <Image
                                        height={200}
                                        style={{ objectFit: 'cover' }}
                                        alt={item.fileId}
                                        src={item.fileUrl}
                                    />
                                }
                                {...(+getUser().role === roles.shopManager && {
                                    actions: [
                                        <UploadFileRC oldFile={item} key="upload" />,
                                        <ModalDelete row={item} onSearch={onSearch} openNotificationWithIcon={openNotificationWithIcon} key="delete" />,
                                    ]
                                })}
                            />
                        </Col>
                    )) :
                        <Empty />
                }
                {
                    (+getUser().role === roles.shopManager) && (
                        <Col span={6} className={cx("card-add")}>
                            <UploadFileRC />
                        </Col>
                    )
                }
            </Row>
        </>
    )
}